import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { type ProductLine, useGetAllProductLines } from "src/api/client-api/productLines/useProductLines";
import { FoodsServerSelectField } from "src/components/EntityForm/FoodsFields";
import type { ProductFormProductLine } from "src/pages/Product/ProductForm/ProductForm.types";

type ProductRangeFieldV2Props = {
    productLineIRI: IRI | null;
    onChange: (productLine: ProductFormProductLine | null) => void;
    label: string;
    placeholder: string;
};

const getProductLineName = (productLine: ProductLine) =>
    productLine.parent ? `${productLine.parent.name} > ${productLine.name}` : productLine.name;
const getProductLineId = (productLine: ProductLine) => productLine["@id"];

export const ProductRangeFieldV2 = ({ productLineIRI, onChange, label, placeholder }: ProductRangeFieldV2Props) => {
    const { t } = useTranslation();
    const { productLines = [], searchApi, isLoading } = useGetAllProductLines();
    const [selectedProductLine, setSelectedProductLine] = useState<ProductLine>();

    useEffect(() => {
        if (productLineIRI && productLines.length > 0) {
            const currentProductLine = productLines.find((productLine) => productLine["@id"] === productLineIRI);
            if (currentProductLine) {
                setSelectedProductLine(currentProductLine);
            }
        } else if (!productLineIRI) {
            setSelectedProductLine(undefined);
        }
    }, [productLineIRI, productLines]);

    const handleChange = (productLine: ProductLine | null) => {
        if (productLine === null) {
            setSelectedProductLine(undefined);
            onChange(null);
            return;
        }

        setSelectedProductLine(productLine);
        onChange({
            "@id": productLine["@id"],
            name: productLine.name,
            parent: productLine.parent,
        });
    };

    return (
        <FoodsServerSelectField<ProductLine>
            appearance="product-v2"
            label={label}
            selectedOption={selectedProductLine ?? null}
            options={isLoading ? [] : productLines}
            onChange={handleChange}
            getId={getProductLineId}
            getName={getProductLineName}
            placeholder={placeholder}
            searchPlaceholder={t("productLine.select.searchPlaceholder", "Search product lines...")}
            emptyOptionsText={t("productLine.select.noOptions", "No product lines available")}
            defaultValueText={t("component.select.defaultvalue")}
            noResultText={
                isLoading ? t("productLine.select.loading", "Loading...") : t("productLine.select.noResultFound")
            }
            search={searchApi.search}
            setSearch={searchApi.setSearch}
            isSearchFound={(value) => searchApi.doesSearchMatch(value)}
        />
    );
};
